import { useCallback, useEffect, useRef } from 'react'
import type { ECharts } from 'echarts'
import { visibleValueExtent, type ScalePoint } from './chartScale'
import { attachValueAxisDrag } from './chartAxisDrag'

interface ZoomWindow {
  id?: string
  xAxisIndex?: number | number[]
  yAxisIndex?: number | number[]
  startValue?: number
  endValue?: number
  start?: number
  end?: number
}

function zoomWindows(chart: ECharts): ZoomWindow[] {
  return chart.getOption().dataZoom as ZoomWindow[] || []
}

/** Fit each panel independently while keeping ECharts' linked time window intact. */
export function useVisibleChartScale(series: readonly (readonly ScalePoint[])[], resetKey: string, preserveTimeRange = false) {
  const chartRef = useRef<ECharts | null>(null)
  const seriesRef = useRef(series)
  const autoFitRef = useRef(false)
  const cleanupRef = useRef<(() => void) | null>(null)
  const refreshRef = useRef<(() => void) | null>(null)
  const cancelDragRef = useRef<(() => void) | null>(null)
  const extentRef = useRef<{
    series: typeof series
    start: number
    end: number
    extent: ReturnType<typeof visibleValueExtent>
  } | null>(null)

  const applyFit = useCallback(() => {
    const chart = chartRef.current
    if (!chart || chart.isDisposed()) return false
    const option = chart.getOption()
    const timeZoom = (option.dataZoom as ZoomWindow[]).find((zoom) => zoom.xAxisIndex != null)
    if (timeZoom?.startValue == null || timeZoom.endValue == null) return false
    const cached = extentRef.current
    if (!cached || cached.series !== seriesRef.current
      || cached.start !== timeZoom.startValue || cached.end !== timeZoom.endValue) {
      extentRef.current = {
        series: seriesRef.current, start: timeZoom.startValue, end: timeZoom.endValue,
        extent: visibleValueExtent(seriesRef.current, timeZoom.startValue, timeZoom.endValue),
      }
    }
    // Reuse the extent when repeated zoom actions keep the same time window.
    const extent = extentRef.current!.extent
    if (!extent) return false
    const axis = (option.yAxis as Array<{ min?: number; max?: number }>)[0]
    if (axis.min !== extent.min || axis.max !== extent.max) {
      // setOption is local: never broadcast one panel's price/OI bounds to its peers.
      chart.setOption({ yAxis: { min: extent.min, max: extent.max } }, { silent: true })
    }
    return true
  }, [])

  const fitVisibleScale = useCallback(() => {
    const chart = chartRef.current
    if (!chart || chart.isDisposed() || !applyFit()) return
    autoFitRef.current = true
    const zooms = zoomWindows(chart)
    chart.setOption({ dataZoom: zooms.map((zoom) => zoom.yAxisIndex != null
      ? { start: 0, end: 100, startValue: null, endValue: null }
      : {}) }, { silent: true })
  }, [applyFit])

  const onChartReady = useCallback((chart: ECharts) => {
    cleanupRef.current?.()
    chartRef.current = chart
    let frame = 0
    const refresh = () => {
      if (!autoFitRef.current || frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        if (autoFitRef.current) applyFit()
      })
    }
    refreshRef.current = refresh

    const isRightAxis = (x: number, y: number) => {
      const timeZoom = zoomWindows(chart).find((zoom) => zoom.xAxisIndex != null)
      if (timeZoom?.startValue == null || timeZoom.endValue == null) return false
      const middle = chart.convertToPixel({ xAxisIndex: 0 }, (timeZoom.startValue + timeZoom.endValue) / 2)
      return Number.isFinite(middle) && x > middle
        && chart.containPixel({ gridIndex: 0 }, [middle, y])
        && !chart.containPixel({ gridIndex: 0 }, [x, y])
    }
    const click = (event: { offsetX: number; offsetY: number; event: MouseEvent }) => {
      if (event.event.shiftKey || event.event.altKey || event.event.ctrlKey || event.event.metaKey) return
      if (isRightAxis(event.offsetX, event.offsetY)) fitVisibleScale()
    }
    const zoom = (raw: unknown) => {
      const event = raw as { dataZoomId?: string; dataZoomIndex?: number; batch?: Array<{ dataZoomId?: string; dataZoomIndex?: number }> }
      const windows = zoomWindows(chart)
      const vertical = (event.batch || [event]).some((entry) => windows.some((window, index) =>
        window.yAxisIndex != null && ((entry.dataZoomId != null && entry.dataZoomId === window.id) || entry.dataZoomIndex === index)))
      // A deliberate vertical zoom remains manual until the next axis click.
      if (vertical) autoFitRef.current = false
      else refresh()
    }
    chart.getZr().on('click', click)
    const axisDrag = attachValueAxisDrag(chart, () => { autoFitRef.current = false }, fitVisibleScale)
    cancelDragRef.current = axisDrag.cancel
    chart.on('datazoom', zoom)
    // Data changes schedule through the effect below. Crosshair redraws must not
    // read getOption(), which clones the complete series history in ECharts.
    cleanupRef.current = () => {
      cancelAnimationFrame(frame)
      axisDrag.dispose()
      cancelDragRef.current = null
      if (!chart.isDisposed()) {
        chart.getZr().off('click', click)
        chart.off('datazoom', zoom)
      }
    }
  }, [applyFit, fitVisibleScale])

  useEffect(() => {
    seriesRef.current = series
    refreshRef.current?.()
  }, [series])

  useEffect(() => {
    cancelDragRef.current?.()
    autoFitRef.current = false
    const chart = chartRef.current
    if (!chart || chart.isDisposed()) return
    chart.setOption({
      yAxis: { min: null, max: null },
      dataZoom: zoomWindows(chart).map(zoom => !preserveTimeRange || zoom.yAxisIndex != null
        ? { start: 0, end: 100, startValue: null, endValue: null } : {}),
    }, { silent: true })
  }, [resetKey, preserveTimeRange])

  useEffect(() => () => cleanupRef.current?.(), [])

  return { fitVisibleScale, onChartReady }
}
